/**
 * Angemeldetes Konto.
 *
 * Die Sitzung steckt in einem HttpOnly-Cookie; die App kann nicht hineinsehen.
 * Ob jemand angemeldet ist, beantwortet ausschließlich der Server über
 * /api/auth/ich. Ein Merker im localStorage wäre eine Vermutung und würde nach
 * Ablauf der Sitzung eine Oberfläche zeigen, die nichts laden kann – genau der
 * Zustand "angemeldet, aber es geht nichts".
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { ApiFehler, api } from '../api/klient'
import type { Anmeldeantwort, EigenesKonto } from '../api/typen'
import { allesVerwerfen } from '../offline/db'

type Zustand = 'prueft' | 'angemeldet' | 'abgemeldet'

/**
 * Bricht ab, wenn etwas zu lange braucht.
 *
 * `fetch` und IndexedDB können beide unbegrenzt hängen. In einem Ablauf, an
 * dessen Ende ein Zustandswechsel steht, wird daraus ein Knopf, der nichts
 * tut.
 */
/* Das zuletzt vom Server bestätigte Konto, für den Start ohne Netz.
 *
 * Bewusst im localStorage und nicht in IndexedDB: es wird beim allerersten
 * Rendern gebraucht, und IndexedDB antwortet asynchron. Es steht darin
 * nichts, was nicht ohnehin auf dem Gerät liegt – Name, Rolle, Rechte,
 * Unterschrift. Kein Kennwort, kein Sitzungsmerkmal; das Sitzungsmerkmal
 * liegt im HttpOnly-Keks, an den auch dieses Programm nicht herankommt.
 *
 * Beim Abmelden wird es gelöscht, zusammen mit den Prüfdaten. */
const ERINNERUNG = 'spuer.letztes-konto'

function merken(konto: EigenesKonto): void {
  try {
    localStorage.setItem(ERINNERUNG, JSON.stringify(konto))
  } catch {
    // Privater Modus oder voller Speicher. Dann eben ohne – die Anwendung
    // verhält sich wie bisher.
  }
}

function erinnerung(): EigenesKonto | null {
  try {
    const roh = localStorage.getItem(ERINNERUNG)
    return roh ? (JSON.parse(roh) as EigenesKonto) : null
  } catch {
    return null
  }
}

function vergessen(): void {
  try {
    localStorage.removeItem(ERINNERUNG)
  } catch {
    // Siehe oben.
  }
}

function mitFrist<T>(versprechen: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    versprechen,
    new Promise<never>((_, ablehnen) =>
      setTimeout(() => ablehnen(new Error(`Zeitüberschreitung nach ${ms} ms`)), ms),
    ),
  ])
}

/** Was der Server zurückgibt, wenn das Passwort stimmt, aber noch ein
    zweiter Faktor fehlt. */
export type Zweitschritt = {
  zweiter_faktor: true
  schritt: string
  /** Welche Wege diesem Konto offenstehen: 'app', 'mail'. */
  arten: string[]
}

type KontoWert = {
  zustand: Zustand
  konto: EigenesKonto | null
  /** Gibt einen Anmeldeschritt zurück, wenn ein zweiter Faktor nötig ist. */
  anmelden: (email: string, passwort: string) => Promise<Zweitschritt | null>
  zweitfaktorAbschliessen: (schritt: string, code: string) => Promise<void>
  abmelden: () => Promise<void>
  /** Nach einer Änderung am eigenen Konto aufrufen – etwa dem Namen. */
  aktualisieren: () => Promise<void>
  /** Gesetzt, wenn beim Abmelden etwas nicht geklappt hat. */
  abmeldehinweis: string | null
  /** Übernimmt die Ansicht eines anderen Benutzers. */
  uebernehmen: (benutzerId: string) => Promise<void>
  /** Zurück zum eigenen Konto. */
  uebernahmeBeenden: () => Promise<void>
  /** Nach einer 401 aus einer beliebigen Anfrage aufrufen. */
  sitzungVerloren: () => void
  /** Wahr, solange die Anmeldung mangels Netz nicht gegengeprüft werden
   *  konnte. Die Oberfläche weist darauf hin; gearbeitet wird trotzdem. */
  ohneGegenprobe: boolean
}

const Kontext = createContext<KontoWert | null>(null)

export function KontoAnbieter({ children }: { children: ReactNode }) {
  const [zustand, setzeZustand] = useState<Zustand>('prueft')
  const [konto, setzeKonto] = useState<EigenesKonto | null>(null)
  const [abmeldehinweis, setzeAbmeldehinweis] = useState<string | null>(null)
  const [ohneGegenprobe, setzeOhneGegenprobe] = useState(false)

  const laden = useCallback(async () => {
    try {
      const eigenes = await api.holen<EigenesKonto>('/api/auth/ich')
      merken(eigenes)
      setzeKonto(eigenes)
      setzeOhneGegenprobe(false)
      setzeZustand('angemeldet')
    } catch (fehler) {
      if (fehler instanceof ApiFehler && fehler.istAnmeldefehler) {
        vergessen()
        setzeKonto(null)
        setzeOhneGegenprobe(false)
        setzeZustand('abgemeldet')
        return
      }

      // Kein Netz. Früher galt das als abgemeldet – und das war der
      // schlimmste Fall, den diese Anwendung kannte: Bernd steht auf einem
      // Platz ohne Empfang, das Handy sperrt, er öffnet die App wieder und
      // bekommt das Anmeldeformular. Anmelden kann er ohne Netz nicht. Die
      // halb fertige Prüfung liegt derweil im Offlinespeicher und ist nicht
      // erreichbar.
      //
      // Stattdessen: das zuletzt bestätigte Konto weiterverwenden. Das gibt
      // niemandem mehr Rechte. Was auf dem Gerät liegt, liegt ohnehin dort,
      // und jede Anfrage an den Server trägt weiterhin das Sitzungsmerkmal
      // und wird dort beurteilt – nicht hier. Sobald wieder Netz da ist,
      // wird gegengeprüft, und eine abgelaufene Sitzung führt dann zum
      // Anmeldeformular.
      const gemerkt = erinnerung()
      if (gemerkt) {
        setzeKonto(gemerkt)
        setzeOhneGegenprobe(true)
        setzeZustand('angemeldet')
        return
      }
      setzeZustand('abgemeldet')
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  // Sobald das Netz wieder da ist, gegenprüfen. Ohne das bliebe eine
  // abgelaufene Sitzung unbemerkt, bis die erste Anfrage scheitert.
  useEffect(() => {
    const an = () => void laden()
    window.addEventListener('online', an)
    return () => window.removeEventListener('online', an)
  }, [laden])

  /* Anmelden kann in einem Schritt enden oder in zweien.
     
     Hat das Konto einen zweiten Faktor, stellt der Server bewusst noch
     keine Sitzung aus, sondern gibt einen Anmeldeschritt zurück. Eine
     Sitzung auszustellen und sie hinterher zu bestätigen wäre falsch
     herum – dann gäbe es ein Zeitfenster, in dem der zweite Faktor nichts
     bedeutet. */
  const anmelden = useCallback(
    async (email: string, passwort: string): Promise<Zweitschritt | null> => {
      const geraet =
        typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 180) : undefined
      const antwort = await api.senden<Anmeldeantwort & Partial<Zweitschritt>>(
        '/api/auth/anmelden',
        { email, passwort, geraet },
      )

      if (antwort.zweiter_faktor) {
        return { zweiter_faktor: true, schritt: antwort.schritt!, arten: antwort.arten! }
      }

      const eigenes = await api.holen<EigenesKonto>('/api/auth/ich')
      merken(eigenes)
      setzeKonto(eigenes)
      setzeZustand('angemeldet')
      return null
    },
    [],
  )

  const zweitfaktorAbschliessen = useCallback(
    async (schritt: string, code: string) => {
      await api.senden('/api/auth/anmelden/zweitfaktor', { schritt, code })
      const eigenes = await api.holen<EigenesKonto>('/api/auth/ich')
      merken(eigenes)
      setzeKonto(eigenes)
      setzeZustand('angemeldet')
    },
    [],
  )

  const abmelden = useCallback(async () => {
    // Abmelden darf an nichts scheitern.
    //
    // Vorher stand `allesVerwerfen()` ungeschützt zwischen dem Klick und dem
    // Zustandswechsel. Schlug der lokale Speicher fehl, brach die Funktion
    // ab, und die beiden Zuweisungen darunter liefen nie – der Knopf tat
    // nach außen nichts, ohne jede Meldung. Ohne Frist konnte er zusätzlich
    // unbegrenzt warten: IndexedDB antwortet auf dem iPhone im installierten
    // Betrieb nicht immer.
    try {
      await mitFrist(api.senden('/api/auth/abmelden'), 8000)
    } catch {
      // Auch ohne Verbindung lokal abmelden.
    }

    // Ein geteiltes Diensthandy darf nach dem Abmelden keine Prüfdaten des
    // Vorgängers mehr enthalten. Klappt das nicht, wird trotzdem abgemeldet –
    // aber es bleibt nicht unerwähnt.
    let hinweis: string | null = null
    try {
      await mitFrist(allesVerwerfen(), 5000)
    } catch (fehler) {
      console.warn('Lokale Prüfdaten konnten nicht gelöscht werden', fehler)
      hinweis =
        'Sie sind abgemeldet. Die auf diesem Gerät zwischengespeicherten ' +
        'Prüfdaten ließen sich allerdings nicht löschen. Auf einem geteilten ' +
        'Gerät bitte die Websitedaten im Browser entfernen.'
    }

    vergessen()
    setzeAbmeldehinweis(hinweis)
    setzeKonto(null)
    setzeOhneGegenprobe(false)
    setzeZustand('abgemeldet')
  }, [])

  // Beim Wechseln der Ansicht muss alles Geladene weg: die Abfragen im
  // Speicher gehören zum vorherigen Blickwinkel, und der Offlinespeicher
  // enthält Prüfbögen, die der andere womöglich gar nicht sehen darf.
  const wechseln = useCallback(async (pfad: string) => {
    const eigenes = await api.senden<EigenesKonto>(pfad)

    // Ab hier hat der Server die Ansicht bereits gewechselt. Was jetzt noch
    // schiefgeht, darf die Oberfläche nicht in der alten Identität stehen
    // lassen – genau das ist einmal passiert: der Server handelte schon als
    // der Übernommene, die Oberfläche zeigte weiter den Administrator, und
    // jeder weitere Druck auf den Knopf lief in „es läuft bereits eine
    // übernommene Ansicht", ohne dass irgendwo zu sehen war, warum.
    //
    // Das Leeren des Offlinespeichers ist genau so eine Stelle: liegt die
    // Datenbank in einem zweiten Tab oder in der installierten App offen,
    // wartet die Transaktion, bis der andere sie freigibt. Deshalb dieselbe
    // Frist wie beim Abmelden.
    try {
      await mitFrist(allesVerwerfen(), 5000)
    } catch (fehler) {
      console.warn('Offlinespeicher beim Ansichtswechsel nicht geleert', fehler)
      // Harter Neustart statt halben Zustands. Danach ist keine Abfrage des
      // vorherigen Blickwinkels mehr im Speicher, und der Versuch, den
      // Offlinespeicher zu leeren, läuft beim Start erneut – ohne den
      // zweiten Tab, der ihn gerade blockiert hat.
      //
      // Prüfbögen, die dabei liegen bleiben, sind kein Datenabfluss: es ist
      // dasselbe Gerät derselben Person und dasselbe Prüfbüro. Es wäre nur
      // verwirrend, und dagegen hilft der Neustart.
      window.location.assign('/')
      return
    }

    merken(eigenes)
    setzeKonto(eigenes)
    setzeZustand('angemeldet')
  }, [])

  const uebernehmen = useCallback(
    (benutzerId: string) => wechseln(`/api/auth/uebernehmen/${benutzerId}`),
    [wechseln],
  )

  const uebernahmeBeenden = useCallback(
    () => wechseln('/api/auth/uebernehmen/beenden'),
    [wechseln],
  )

  const sitzungVerloren = useCallback(() => {
    vergessen()
    setzeKonto(null)
    setzeOhneGegenprobe(false)
    setzeZustand('abgemeldet')
  }, [])

  const wert = useMemo<KontoWert>(
    () => ({
      zustand,
      konto,
      anmelden,
      zweitfaktorAbschliessen,
      abmelden,
      aktualisieren: laden,
      abmeldehinweis,
      uebernehmen,
      uebernahmeBeenden,
      sitzungVerloren,
      ohneGegenprobe,
    }),
    [
      zustand,
      konto,
      anmelden,
      zweitfaktorAbschliessen,
      abmelden,
      laden,
      abmeldehinweis,
      uebernehmen,
      uebernahmeBeenden,
      sitzungVerloren,
      ohneGegenprobe,
    ],
  )

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>
}

export function useKonto(): KontoWert {
  const wert = useContext(Kontext)
  if (!wert) throw new Error('useKonto braucht den KontoAnbieter')
  return wert
}

/**
 * Darf der angemeldete Benutzer das?
 *
 * Gefragt wird nach dem Recht und nicht nach der Rolle. Der Server rechnet
 * die Rechte aus Rolle und Abweichungen aus und schickt sie mit dem Konto;
 * hier wird nur nachgesehen.
 *
 * Genau daran ist es einmal gescheitert: die Rollenliste im Browser wich
 * von der im Server ab, Knöpfe verschwanden, die der Server erlaubt hätte,
 * und niemand konnte erkennen, warum. Mit einer Liste, die vom Server
 * kommt, kann das nicht mehr auseinanderlaufen.
 */
export function darf(konto: EigenesKonto | null, recht: string): boolean {
  return konto !== null && (konto.rechte ?? []).includes(recht)
}

export function darfPruefen(konto: EigenesKonto | null): boolean {
  return darf(konto, 'pruefen')
}

export function darfSchreiben(konto: EigenesKonto | null): boolean {
  return darf(konto, 'kunden_pflegen')
}

export function darfVerwalten(konto: EigenesKonto | null): boolean {
  return darf(konto, 'einstellungen_verwalten')
}

export function darfBenutzer(konto: EigenesKonto | null): boolean {
  return darf(konto, 'benutzer_verwalten')
}

export function darfSystem(konto: EigenesKonto | null): boolean {
  return darf(konto, 'system_verwalten')
}
